import { createTheme } from '@mui/material/styles';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5A8A7F', // Teal accent for light mode
      light: '#6F9F95',
      dark: '#4A7A6F',
    },
    secondary: {
      main: '#495057',
      light: '#868E96',
      dark: '#343A40',
    },
    success: {
      main: '#27AE60',
      light: '#2ECC71',
      dark: '#219A52',
    },
    warning: {
      main: '#D68910',
      light: '#F39C12',
      dark: '#B9770E',
    },
    error: {
      main: '#C0392B',
      light: '#E74C3C',
      dark: '#A93226',
    },
    info: {
      main: '#5A8A7F',
      light: '#6F9F95',
      dark: '#4A7A6F',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F8F9FA',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#495057',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Montserrat", "Roboto", "Helvetica", "Arial", sans-serif', // Poppins as primary from brand guide
    h1: {
      fontSize: '2.75rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontFamily: '"Poppins", sans-serif',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#333132',
      fontFamily: '"Poppins", sans-serif',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#B2BBC7',
      fontFamily: '"Poppins", sans-serif',
    },
    subtitle1: {
      fontWeight: 500,
      letterSpacing: '0.025em',
      fontSize: '1rem',
      color: '#333132',
      fontFamily: '"Poppins", sans-serif',
    },
    subtitle2: {
      fontWeight: 500,
      letterSpacing: '0.025em',
      fontSize: '0.875rem',
      color: '#B2BBC7',
      fontFamily: '"Poppins", sans-serif',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.025em',
      fontSize: '0.875rem',
      fontFamily: '"Poppins", sans-serif',
    },
  },
  shape: {
    borderRadius: 16, // Modern rounded corners
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 600,
          boxShadow: 'none',
          fontFamily: '"Poppins", sans-serif',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
          transition: 'all 0.2s ease',
        },
        contained: {
          background: '#5A8A7F',
          color: '#FFFFFF',
          '&:hover': {
            background: '#4A7A6F',
          },
          '&.Mui-disabled': {
            background: '#E9ECEF',
            color: '#868E96',
          },
        },
        outlined: {
          borderColor: '#DEE2E6',
          color: '#495057',
          '&:hover': {
            borderColor: '#5A8A7F',
            backgroundColor: 'rgba(90, 138, 127, 0.08)',
          },
        },
        text: {
          color: '#495057',
          '&:hover': {
            backgroundColor: 'rgba(90, 138, 127, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #DEE2E6',
          background: '#FFFFFF',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#F8F9FA',
            fontFamily: '"Poppins", sans-serif',
            '& fieldset': {
              borderColor: '#DEE2E6',
            },
            '&:hover fieldset': {
              borderColor: '#5A8A7F',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#5A8A7F',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#F8F9FA',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          fontFamily: '"Poppins", sans-serif',
          backgroundColor: '#E9ECEF',
          color: '#495057',
          border: '1px solid #DEE2E6',
          '&:hover': {
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F9FA',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#1A1A1A',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
            fontFamily: '"Poppins", sans-serif',
            borderBottom: '1px solid #DEE2E6',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
          borderBottom: '1px solid #DEE2E6',
          fontFamily: '"Poppins", sans-serif',
          color: '#1A1A1A',
        },
        head: {
          fontWeight: 600,
          color: '#1A1A1A',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(90, 138, 127, 0.05)',
            transform: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          border: '1px solid #DEE2E6',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          background: 'transparent',
          padding: '24px 0',
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .Mui-completed': {
            color: '#5A8A7F',
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: '#5A8A7F',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-completed': {
            color: '#5A8A7F',
          },
          '&.Mui-active': {
            color: '#5A8A7F',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontFamily: '"Poppins", sans-serif',
        },
        standardSuccess: {
          backgroundColor: 'rgba(39, 174, 96, 0.1)',
          color: '#27AE60',
          border: '1px solid rgba(39, 174, 96, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(90, 138, 127, 0.1)',
          color: '#5A8A7F',
          border: '1px solid rgba(90, 138, 127, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(214, 137, 16, 0.1)',
          color: '#D68910',
          border: '1px solid rgba(214, 137, 16, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(192, 57, 43, 0.1)',
          color: '#C0392B',
          border: '1px solid rgba(192, 57, 43, 0.3)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#FFFFFF',
          borderRight: '1px solid #DEE2E6',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid #DEE2E6',
          boxShadow: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(90, 138, 127, 0.08)',
            transform: 'none',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(90, 138, 127, 0.12)',
            borderLeft: '3px solid #5A8A7F',
            '&:hover': {
              backgroundColor: 'rgba(90, 138, 127, 0.16)',
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#EF4444',
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: '0.75rem',
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #DEE2E6',
        },
        indicator: {
          backgroundColor: '#5A8A7F',
          height: 3,
          borderRadius: '2px 2px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          fontFamily: '"Poppins", sans-serif',
          minHeight: 48,
          '&.Mui-selected': {
            color: '#5A8A7F',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            color: '#868E96',
            fontWeight: 500,
            fontFamily: '"Poppins", sans-serif',
            '&.Mui-focused': {
              color: '#5A8A7F',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#868E96',
          '&.Mui-checked': {
            color: '#5A8A7F',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#868E96',
          '&.Mui-checked': {
            color: '#5A8A7F',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#5A8A7F',
            '& + .MuiSwitch-track': {
              backgroundColor: '#5A8A7F',
            },
          },
        },
        track: {
          backgroundColor: '#DEE2E6',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #DEE2E6',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8F9FA',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(90, 138, 127, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(90, 138, 127, 0.15)',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #DEE2E6',
        },
        option: {
          '&:hover': {
            backgroundColor: '#F8F9FA',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(90, 138, 127, 0.1)',
          },
        },
      },
    },
  },
});

// Dark theme with MedNecessity colors
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6F9F95', // Teal accent for dark mode
      light: '#8AB5AC',
      dark: '#5A8A80',
    },
    secondary: {
      main: '#B2BBC7',
      light: '#FFFFFF',
      dark: '#333132',
    },
    background: {
      default: '#18181B', // Surface onyx
      paper: '#1E1E22', // Surface carbon
    },
    text: {
      primary: '#E8E8E8',
      secondary: '#A0A0A0',
    },
    success: {
      main: '#6F9F95',
    },
    warning: {
      main: '#D4A574',
    },
    error: {
      main: '#9E6F6F',
    },
  },
  typography: lightTheme.typography,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 600,
          boxShadow: 'none',
          fontFamily: '"Poppins", sans-serif',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
        },
        contained: {
          background: '#6F9F95',
          color: '#18181B',
          '&:hover': {
            background: '#8AB5AC',
          },
          '&.Mui-disabled': {
            background: '#2A2A2E',
            color: '#666666',
          },
        },
        outlined: {
          borderColor: '#3A3A3E',
          color: '#A0A0A0',
          '&:hover': {
            borderColor: '#6F9F95',
            backgroundColor: 'rgba(111, 159, 149, 0.1)',
          },
        },
        text: {
          color: '#A0A0A0',
          '&:hover': {
            backgroundColor: '#2A2A2E',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: '#1E1E22',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E22',
          border: '1px solid #2A2A2E',
          borderRadius: 12,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#1E1E22',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid #2A2A2E',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#1E1E22',
          borderRight: '1px solid #2A2A2E',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#2A2A2E',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: '#3A3A3E',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6F9F95',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666666',
          },
          '& .MuiInputBase-input': {
            color: '#E8E8E8',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#2A2A2E',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: 'rgba(111, 159, 149, 0.1)',
          color: '#6F9F95',
          border: '1px solid rgba(111, 159, 149, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(111, 159, 149, 0.1)',
          color: '#6F9F95',
          border: '1px solid rgba(111, 159, 149, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(212, 165, 116, 0.1)',
          color: '#D4A574',
          border: '1px solid rgba(212, 165, 116, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(158, 111, 111, 0.1)',
          color: '#9E6F6F',
          border: '1px solid rgba(158, 111, 111, 0.3)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(111, 159, 149, 0.1)',
            transform: 'none',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(111, 159, 149, 0.15)',
            borderLeft: '3px solid #6F9F95',
            '&:hover': {
              backgroundColor: 'rgba(111, 159, 149, 0.2)',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#666666',
          '&.Mui-checked': {
            color: '#6F9F95',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#666666',
          '&.Mui-checked': {
            color: '#6F9F95',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#6F9F95',
            '& + .MuiSwitch-track': {
              backgroundColor: '#6F9F95',
            },
          },
        },
        track: {
          backgroundColor: '#3A3A3E',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: '#2A2A2E',
          color: '#A0A0A0',
          border: '1px solid #3A3A3E',
          '&:hover': {
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E22',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#A0A0A0',
            borderBottom: '1px solid #2A2A2E',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #2A2A2E',
          color: '#E8E8E8',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(111, 159, 149, 0.05)',
            transform: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: '#1E1E22',
          border: '1px solid #2A2A2E',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          background: 'transparent',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#2A2A2E',
          '&.Mui-completed': {
            color: '#6F9F95',
          },
          '&.Mui-active': {
            color: '#6F9F95',
          },
        },
        text: {
          fill: '#E8E8E8',
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: '#2A2A2E',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#666666',
          '&.Mui-focused': {
            color: '#6F9F95',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: '#E8E8E8',
        },
        input: {
          '&::placeholder': {
            color: '#666666',
            opacity: 1,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E22',
          border: '1px solid #2A2A2E',
        },
        option: {
          '&:hover': {
            backgroundColor: '#2A2A2E',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(111, 159, 149, 0.15)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E22',
          border: '1px solid #2A2A2E',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#E8E8E8',
          '&:hover': {
            backgroundColor: '#2A2A2E',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(111, 159, 149, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(111, 159, 149, 0.2)',
            },
          },
        },
      },
    },
  },
});

export { lightTheme, darkTheme }; 